"use strict";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { onReady } from "../core/ready.js";
import { enhanceInlineLines } from "../core/inline-line-svg.js";

gsap.registerPlugin(ScrollTrigger, SplitText);

const LINE_STAGGER = 0.1;
const LINE_DURATION = 0.85;
const TITLE_DURATION = 1.8;

function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function waitForFonts() {
    if (!document.fonts?.ready) {
        return Promise.resolve();
    }
    return document.fonts.ready.catch(() => {});
}

function getSplitTargets(description) {
    const paragraphs = Array.from(description.querySelectorAll("p")).filter(
        (p) => (p.textContent || "").trim().length > 0
    );
    return paragraphs.length ? paragraphs : [description];
}

function setupDescriptionLines(description) {
    const targets = getSplitTargets(description);
    let hasPlayed = false;

    const runEnhance = () => {
        enhanceInlineLines(description, { ignoreNoInject: true });
    };

    if (prefersReducedMotion()) {
        description.classList.add("is-lines-ready");
        runEnhance();
        return null;
    }

    gsap.set(targets, { autoAlpha: 0 });

    return SplitText.create(targets, {
        type: "lines",
        linesClass: "claim-line",
        autoSplit: true,
        onSplit(self) {
            runEnhance();
            description.classList.add("is-lines-ready");
            gsap.set(targets, { autoAlpha: 1 });

            if (hasPlayed) {
                gsap.set(self.lines, { opacity: 1, y: 0 });
                return;
            }

            gsap.set(self.lines, { opacity: 0, y: 40 });

            return gsap.to(self.lines, {
                opacity: 1,
                y: 0,
                duration: LINE_DURATION,
                stagger: LINE_STAGGER,
                ease: "power2.out",
                overwrite: "auto",
                scrollTrigger: {
                    trigger: description,
                    start: "top 85%",
                    once: true,
                },
                onComplete: () => {
                    hasPlayed = true;
                    gsap.set(self.lines, { clearProps: "transform" });
                },
            });
        },
    });
}

function setupTitleReveal(title) {
    if (prefersReducedMotion()) {
        gsap.set(title, { clearProps: "opacity,transform" });
        title.classList.add("is-revealed");
        return;
    }

    gsap.set(title, {
        opacity: 0,
        yPercent: 18,
        scale: 1.08,
        transformOrigin: "50% 100%",
    });

    gsap.to(title, {
        opacity: 1,
        yPercent: 0,
        scale: 1,
        duration: TITLE_DURATION,
        ease: "power2.out",
        overwrite: "auto",
        scrollTrigger: {
            trigger: title,
            start: "top 92%",
            once: true,
        },
        onComplete: () => {
            title.classList.add("is-revealed");
            gsap.set(title, { clearProps: "transform" });
        },
    });
}

function initClaimPage() {
    const main = document.querySelector(".claim-main");
    if (!main) return;

    const description = main.querySelector(".claim-description");
    const title = main.querySelector(".claim-title");

    waitForFonts().then(() => {
        if (description) {
            setupDescriptionLines(description);
        }
        if (title) {
            setupTitleReveal(title);
        }
        requestAnimationFrame(() => ScrollTrigger.refresh());
    });
}

onReady(() => {
    initClaimPage();
});
